import type { Post } from "@core/blog/types";
import { Assessment } from "../../assessments/types";
import { Course } from "../../courses/types";
import { Grade, GradeCategory, Lesson, Subject } from "../../grades/types";
import { Offer } from "../../offers/types";

export const URLS = {
  notifications: {
    root: "/notifications",
    all: "/notifications/all",
    unreadCount: "/notifications/unread-count",
    preferences: "/notifications/preferences",
    pushSubscription: "/notifications/push-subscription",
    action: "/notifications",
  },
  home: "/",
  feed: "/feed",
  gamification: "/my-progress",
  gamificationTasks: {
    api: "/my-progress/tasks",
    unclaimedCount: "/my-progress/tasks/unclaimed-count",
  },
  goldStore: "/gold-store",
  achievements: "/achievements",
  certificates: {
    list: "/certificates",
    view: (id: number | string) => `/certificates/${id}`,
    download: (id: number | string, format: "png" | "pdf") =>
      `/certificates/${id}/download?format=${format}`,
    hallOfFame: "/certificates/hall-of-fame",
    verify: (code: string) =>
      `/certificates/verify/${encodeURIComponent(code)}`,
  },
  communityGroups: {
    list: "/community-groups",
    view: (id: number | string) => `/community-groups/${id}`,
    post: (groupId: number | string, postId: number | string) =>
      `/community-groups/${groupId}/posts/${postId}`,
  },
  inquiries: {
    list: "/inquiries",
    new: "/inquiries/new",
    unread: "/inquiries/unread",
    view: (id: number | string) => `/inquiries/${id}`,
    api: "/inquiries",
    apiView: (id: number | string) => `/inquiries/${id}`,
    apiMessages: (id: number | string) => `/inquiries/${id}/messages`,
    apiRead: (id: number | string) => `/inquiries/${id}/read`,
    apiClose: (id: number | string) => `/inquiries/${id}/close`,
    apiUnreadCount: "/inquiries/unread-count",
  },
  notFound: "/404",
  maintenance: "/maintenance",
  lessons: {
    list: "/lessons",
    myLessons: "/my-lessons",
    view: (lesson: Lesson) => `/lessons/${lesson.id}`,
    like: (lessonId: number) => `/lessons/${lessonId}/like`,
  },
  subjects: {
    list: "/subjects",
    view: (subject: Subject) => `/subjects/${subject.id}`,
  },
  grades: {
    list: "/grades",
    view: (grade: Grade) => `/grades/${grade.id}`,
    categories: "/grades/categories",
    viewCategory: (category: GradeCategory) =>
      `/grades/categories/${category.id}`,
  },
  assessments: {
    list: "/assessments",
    api: "/assessments/api",
    history: "/assessments/history",
    historyQuizOptions: "/assessments/history/options/quizzes",
    historyView: (attemptId: number) => `/assessments/history/${attemptId}`,
    mistakeAnalysis: (quizId: number) =>
      `/assessments/${quizId}/mistake-analysis`,
    view: (assessment: Assessment) => `/assessments/${assessment.id}`,
  },
  trainingGround: "/training-ground",
  league: "/league",
  leaderboard: {
    topAssessments: "/leaderboard/top-assessments",
  },
  challenges: {
    list: "/challenges",
    view: (id: string | number) => `/challenges/${id}`,
  },
  offers: {
    list: "/offers",
    api: "/offers/api",
    view: (offer: Offer) => `/offers/${offer.id}`,
  },
  courses: {
    list: "/courses",
    view: (course: Course) => `/courses/${course.id}`,
  },
  onlineSessions: {
    list: "/online-sessions",
    view: (id: string | number) => `/online-sessions/${id}`,
  },
  sessions: {
    history: "/my-sessions",
    api: "/session-students",
    /** Web resource route for the unpaid monthly sessions strip. */
    dues: "/my-sessions/dues",
    duesApi: "/me/offline-dues",
    invoices: "/my-invoices",
    invoicesApi: "/me/invoices",
  },
  reports: {
    list: "/reports",
    api: "/me/progress-reports",
    generate: "/me/progress-reports/generate",
    backendPdf: (id: number | string) => `/me/progress-reports/${id}/pdf`,
    pdf: (id: number | string) => `/reports/${id}/pdf`,
  },
  parentPortal: {
    access: "/parent-portal",
    sessionEnded: "/parent-portal?notice=session-ended",
    link: "/parent-portal/link",
    report: "/parent-portal/report",
    logout: "/parent-portal/logout/action",
  },
  checkout: {
    root: "/checkout",
    failedPayment: "/checkout/failed-payment",
    successPayment: (id: string) => `/checkout/success-payment/${id}`,
    successPaymentRoute: "/checkout/success-payment/:id",
    paymentStatus: "/checkout/status",
  },
  unlockCode: {
    root: "/unlock-code",
    redeem: (code: string) => `/unlock-code/${code}`,
    redeemRoute: "/unlock-code/:code",
  },
  orderCompleted: "/order-completed",
  successCheckout: (order: any) => `/checkout/success/${order.id}`,
  paymentGate: (url: string) => `/checkout/payment-gate/${url}`,
  successCheckoutRoute: "/checkout/success/:id",
  paymentGateRoute: "/checkout/payment-gate/:id",
  blog: {
    list: "/blog",
    viewRoute: "/blog/:id/:slug",
    view: (post: Post) => `/blog/${post.id}/${post.slug}`,
  },
  faq: "/faq",
  contactUs: "/contact-us",
  aboutUs: "/about-us",
  privacyPolicy: "/privacy-policy",
  termsOfService: "/terms-of-service",
  account: {
    root: "/account",
    profile: "/account",
    edit: "/account/edit",
    notifications: "/account/notifications",
    connections: "/account/connections",
    security: "/account/security",
    changePassword: "/account/change-password",
    updatePassword: "/account/update-password",
    deleteAccount: "/account/delete-account",
    deleteAccountCode: "/account/delete-account/code",
  },
  auth: {
    login: "/auth/login",
    loginWithReturnTo: (returnTo: string) =>
      `/auth/login?returnTo=${encodeURIComponent(returnTo)}`,
    register: "/auth/register",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    verifyCode: "/auth/verify-code",
  },
  returnPolicy: "/return-policy",
  termsConditions: "/terms-conditions",
  comments: {
    root: "/comments",
    addComment: "/comments/add-comment",
    updateComment: "/comments/update-comment",
  },
  ratings: {
    root: "/ratings",
    update: "/ratings/update",
  },
};

/**
 * Returns a same-site path that is safe to use after authentication.
 */
export function getSafeAuthReturnPath(
  returnTo: string | null | undefined,
): string | undefined {
  if (
    !returnTo ||
    !returnTo.startsWith("/") ||
    returnTo.startsWith("//") ||
    returnTo.startsWith(URLS.auth.login)
  ) {
    return undefined;
  }

  return returnTo;
}
